"use client";
/**
 * SANATAN DECODE — shared motion kit.
 *
 * Everything v10/v11/v12 had copy-pasted between them lives here once: the laws (on twos,
 * swing physics, the unmount gate), the ground, the type, the persistent thread, and the
 * two collage materials (torn paper card, photo sticker). A version file should now contain
 * ONLY its beats and its own signature idea.
 *
 * ⛔ LAWS baked in, do not re-litigate per version:
 *   1 ANIMATE ON TWOS            — useStep() everywhere, never useCurrentFrame()
 *   2 NOTHING FADES, IT SWINGS   — damping 8-9 in, damping 200 out
 *   3 OBJECT PERMANENCE          — persistent objects live at stage level, outside <Beat>
 *   4 ONE ACCENT PER BEAT        — KUMKUM on light, AMBER on dark
 *   5 BG NEVER MERGES WITH FG    — blur the ground, and give the FG a material edge
 *   6 TEXT ANCHORED TO THE WORD  — W[] holds real ElevenLabs timestamps
 *   7 ANCHOR FIRST               — frame 0 opens on the evidence
 *   8 MORE SHAPES NEVER HELPS    — fix motion or the plate, never add vector furniture
 *   ⛔ every hook goes ABOVE the `return null` unmount gate, or the render dies at frame 0
 *   ⛔ Devanagari: NEVER Inter (renders □□□), NEVER letterSpacing (shears the shirorekha)
 */
import React from "react";
import {
  AbsoluteFill, Img, staticFile,
  useCurrentFrame, useVideoConfig, interpolate, spring, Easing, random,
} from "remotion";
import { loadFont as loadDeva } from "@remotion/google-fonts/NotoSansDevanagari";
import { loadFont as loadInter } from "@remotion/google-fonts/Inter";

const { fontFamily: devaBase } = loadDeva("normal", { weights: ["400", "600", "700", "800", "900"], subsets: ["devanagari", "latin"] });
const { fontFamily: interBase } = loadInter("normal", { weights: ["500", "700", "800"], subsets: ["latin"] });

/** Devanagari first, Inter only as the fallback for stray Latin punctuation. */
export const deva = `${devaBase}, ${interBase}, sans-serif`;
/** Latin only. Never put Devanagari in this. */
export const inter = interBase;

export const INK = "#22304F";
export const GOLD = "#BE9433";
export const MARIGOLD = "#D98324";
export const KUMKUM = "#C0392B";
export const MUTED_C = "#6A6250";
export const CREAM = "#F7EFDD";
export const PAPER = "#FBF3E0";
export const PAPER_EDGE = "#FFFDF6";

export const FPS = 30;
export const f = (s: number) => s * FPS;
export const useStep = () => Math.floor(useCurrentFrame() / 2) * 2;

/** Real ElevenLabs word timings for the gotra proof narration. */
export const W: Record<number, [number, number, string]> = {
  0: [0.0, 0.15, "आप"], 1: [0.21, 0.35, "जिस"], 2: [0.41, 0.71, "गोत्र"], 3: [0.74, 0.82, "का"],
  4: [0.87, 1.06, "नाम"], 5: [1.13, 1.36, "लेते"], 6: [1.42, 1.64, "हैं"],
  8: [2.03, 2.16, "वो"], 9: [2.26, 2.47, "आपका"], 10: [2.58, 3.01, "सरनेम"], 11: [3.05, 3.24, "नहीं"],
  12: [3.32, 3.6, "है।"],
  15: [4.35, 4.7, "ऋषि"],
  26: [8.2, 8.42, "एक"], 27: [8.45, 8.52, "ही"], 28: [8.63, 9.17, "रक्त-रेखा"],
  34: [11.61, 12.03, "शास्त्र"],
  38: [13.41, 14.03, "सगोत्र"], 39: [14.41, 14.76, "विवाह"], 40: [14.86, 15.27, "नहीं।"],
  45: [17.43, 17.86, "विज्ञान"],
};

/** The five beat windows the audio actually has. */
export const BEATS: [number, number][] = [[0, 3.75], [3.9, 5.6], [5.8, 10.4], [10.6, 15.6], [15.8, 21.1]];

export const useKeys = (keys: [number, number][]) => {
  const frame = useStep();
  return interpolate(frame, keys.map((k) => f(k[0])), keys.map((k) => k[1]), {
    extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.inOut(Easing.cubic),
  });
};

export const useSpringAt = (at: number, damping = 9, stiffness = 110, mass = 1.1) => {
  const frame = useStep();
  const { fps } = useVideoConfig();
  return spring({ frame: frame - f(at), fps, config: { damping, stiffness, mass } });
};

/* ══ THE GROUND ════════════════════════════════════════════════ */
export const PANO_W = 3840, PANO_H = 1280;
/** Asymmetric walk: fast L→R sprint across every cut, slow R→L reclaim while a beat sits. */
export const WALK: [number, number][] = [
  [0.00, 0.0000], [3.60, 0.0492], [4.15, 0.4180], [5.45, 0.3443], [6.05, 0.8156],
  [10.25, 0.4795], [10.85, 0.8893], [15.45, 0.5205], [16.05, 1.0000], [21.20, 0.8770],
];
export const walkAt = (fr: number, walk = WALK) =>
  interpolate(fr, walk.map((k) => f(k[0])), walk.map((k) => k[1]), {
    extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.inOut(Easing.cubic),
  });

export const Pano: React.FC<{
  src?: string; displayH?: number; viewW?: number; top?: number;
  blur?: number; sat?: number; bright?: number; contrast?: number;
  wash?: number; vignette?: boolean; walk?: [number, number][];
}> = ({
  src = "sanatan/pano/gurukul_pano.png", displayH = 1240, viewW = 1280, top = -300,
  blur = 4.2, sat = 0.62, bright = 1.12, contrast = 0.84, wash = 0.32, vignette = true, walk = WALK,
}) => {
    const frame = useStep();
    const dispW = (PANO_W / PANO_H) * displayH;
    const travel = dispW - viewW;
    const x = walkAt(frame, walk);
    const vx = (Math.abs(x - walkAt(Math.max(0, frame - 2), walk)) * travel) / 2;
    const whip = Math.min(20, vx * 0.16);
    const y = Math.sin(frame / 78) * 7;
    return (
      <AbsoluteFill style={{ backgroundColor: "#F2E6CC", overflow: "hidden" }}>
        <Img src={staticFile(src)} style={{
          position: "absolute", height: displayH, width: dispW,
          left: -x * travel, top: top + y, maxWidth: "none",
          filter: `blur(${(blur + whip).toFixed(2)}px) saturate(${sat}) brightness(${bright}) contrast(${contrast})`,
        }} />
        <AbsoluteFill style={{ backgroundColor: `rgba(249,240,222,${wash})` }} />
        {vignette && <AbsoluteFill style={{ background: "radial-gradient(ellipse 84% 78% at 50% 46%, rgba(255,252,242,0.32) 0%, rgba(198,158,100,0.32) 100%)" }} />}
      </AbsoluteFill>
    );
  };

export const Grain: React.FC<{ op?: number }> = ({ op = 0.13 }) => {
  const frame = useStep();
  return (
    <AbsoluteFill style={{ pointerEvents: "none", opacity: op, mixBlendMode: "multiply" }}>
      <svg width="100%" height="100%">
        <filter id="grk"><feTurbulence type="fractalNoise" baseFrequency="0.92" numOctaves="3" seed={frame % 5} /></filter>
        <rect width="100%" height="100%" filter="url(#grk)" />
      </svg>
    </AbsoluteFill>
  );
};

export const Petals: React.FC<{ n?: number; op?: number; color?: string }> = ({ n = 14, op = 0.22, color = MARIGOLD }) => {
  const frame = useStep();
  return (
    <AbsoluteFill style={{ pointerEvents: "none" }}>
      {Array.from({ length: n }, (_, i) => {
        const seed = `k${i}`;
        const x = random(seed) * 1280;
        const sp = 0.4 + random(seed + "s") * 0.7;
        const y = ((random(seed + "y") * 800 + frame * sp) % 830) - 60;
        const sway = Math.sin((frame + i * 40) / 34) * 30;
        const r = 3 + random(seed + "r") * 4.5;
        return <div key={i} style={{
          position: "absolute", left: x + sway, top: y, width: r * 2, height: r * 1.25,
          borderRadius: "50%", background: color, opacity: op,
          transform: `rotate(${frame * 1.4 + i * 30}deg)`,
        }} />;
      })}
    </AbsoluteFill>
  );
};

/* ══ TYPE ══════════════════════════════════════════════════════ */
export const Word: React.FC<{
  idx: number; size: number; color?: string; weight?: number;
  strike?: boolean; underline?: boolean; outAt?: number; halo?: boolean;
}> = ({ idx, size, color = INK, weight = 800, strike, underline, outAt, halo = true }) => {
  const [start] = W[idx];
  const frame = useStep();
  const { fps } = useVideoConfig();
  const sIn = spring({ frame: frame - f(start), fps, config: { damping: 9, stiffness: 150, mass: 0.8 } });
  const sOut = outAt ? spring({ frame: frame - f(outAt), fps, config: { damping: 200, stiffness: 80, mass: 0.5 } }) : 0;
  const grow = interpolate(frame, [f(start) + 2, f(start) + 12], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.out(Easing.cubic) });
  return (
    <span style={{
      position: "relative", display: "inline-block",
      fontFamily: deva, fontWeight: weight, fontSize: size, lineHeight: 1.34, color,
      textShadow: halo ? "0 0 26px rgba(255,251,240,0.98), 0 0 10px rgba(255,251,240,0.95), 0 2px 2px rgba(255,251,240,0.8)" : undefined,
      opacity: Math.min(1, sIn * 1.5) * (1 - Math.min(1, sOut * 1.4)),
      transform: `translateY(${(1 - sIn) * 32}px) rotate(${8 * (1 - sIn) - 13 * sOut}deg) scale(${0.87 + sIn * 0.13})`,
      transformOrigin: "50% 140%",
    }}>
      {W[idx][2]}
      {strike && <span style={{ position: "absolute", left: -4, top: "53%", height: Math.max(4, size * 0.075), width: `calc((100% + 8px) * ${grow})`, background: KUMKUM, borderRadius: 4 }} />}
      {underline && <span style={{ position: "absolute", left: 0, bottom: -6, height: Math.max(4, size * 0.06), width: `calc(100% * ${grow})`, background: GOLD, borderRadius: 3, opacity: 0.85 }} />}
    </span>
  );
};

export const Row: React.FC<{ children: React.ReactNode; gap?: number }> = ({ children, gap = 16 }) => (
  <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: `0 ${gap}px` }}>{children}</div>
);

/** Free Devanagari text (not word-anchored) that still obeys the swing law. */
export const Line: React.FC<{
  text: string; at: number; outAt?: number; size?: number; color?: string; weight?: number; halo?: boolean;
}> = ({ text, at, outAt, size = 24, color = MUTED_C, weight = 700, halo = true }) => {
  const sIn = useSpringAt(at, 9, 160, 0.8);
  const sOut = useSpringAt(outAt ?? 1e6, 200, 80, 0.5);
  return (
    <span style={{
      display: "inline-block", fontFamily: deva, fontSize: size, fontWeight: weight, color,
      textShadow: halo ? "0 0 22px rgba(255,251,240,0.98), 0 0 9px rgba(255,251,240,0.95)" : undefined,
      opacity: Math.min(1, sIn * 1.5) * (1 - Math.min(1, sOut * 1.4)),
      transform: `translateY(${(1 - sIn) * 18 + sOut * 60}px) rotate(${6 * (1 - sIn)}deg)`,
      transformOrigin: "50% 140%",
    }}>{text}</span>
  );
};

/* ══ THE UNMOUNT GATE ══════════════════════════════════════════
   Lead-in/lead-out are deliberate: the outgoing beat is still swinging out while the next is
   swinging in, so there are no dead frames at a cut. ⛔ <Sequence> would rebase the frame. */
export const Beat: React.FC<{ from: number; to: number; children: React.ReactNode }> = ({ from, to, children }) => {
  const frame = useStep();
  if (frame < f(from - 0.55) || frame >= f(to + 0.7)) return null;
  return <AbsoluteFill>{children}</AbsoluteFill>;
};

export const CAM: [number, number, number, number][] = [
  [0, 1.09, 0, 26], [1.4, 1.0, 0, 0], [3.92, 1.06, 22, -12], [5.82, 1.0, -18, 8],
  [10.58, 1.05, 12, -10], [15.76, 1.0, -14, 6], [21.1, 1.08, 8, -8],
];
export const useCamera = (cam = CAM) => {
  const frame = useStep();
  const t = cam.map((c) => f(c[0]));
  const e = { extrapolateLeft: "clamp" as const, extrapolateRight: "clamp" as const, easing: Easing.inOut(Easing.quad) };
  return `translate(${interpolate(frame, t, cam.map((c) => c[2]), e)}px, ${interpolate(frame, t, cam.map((c) => c[3]), e)}px) scale(${interpolate(frame, t, cam.map((c) => c[1]), e)})`;
};

/* ══ SWING ═════════════════════════════════════════════════════ */
export type SwingCfg = { inAt: number; outAt: number; from?: "left" | "right" | "top"; amp?: number; travel?: number };
export const useSwing = ({ inAt, outAt, from = "left", amp = 17, travel = 240 }: SwingCfg) => {
  const frame = useStep();
  const sIn = useSpringAt(inAt, 8, 95, 1.15);
  const sOut = useSpringAt(outAt, 200, 75, 0.5);
  const sign = from === "right" ? -1 : 1;
  const pend = Math.sin((frame - f(inAt)) / 34) * 1.4 * sIn * (1 - sOut);
  const dx = from === "top" ? 0 : travel * sign * (1 - sIn) - travel * 1.5 * sign * sOut;
  const dy = from === "top" ? -travel * (1 - sIn) + travel * 2 * sOut : 70 * (1 - sIn) + 190 * sOut;
  return {
    transform: `translate(${dx}px, ${dy}px) rotate(${amp * sign * (1 - sIn) + pend - 30 * sign * sOut}deg) scale(${0.9 + 0.1 * sIn - 0.12 * sOut})`,
    transformOrigin: "50% -28%",
    opacity: Math.min(1, sIn * 1.6) * (1 - Math.min(1, sOut * 1.4)),
  } as React.CSSProperties;
};

export const PROP_SHADOW = "drop-shadow(0 24px 26px rgba(72,46,16,0.40)) drop-shadow(0 2px 3px rgba(72,46,16,0.30))";

/** An illustrated cutout — the CLAIM register. */
export const Prop: React.FC<{ src: string; h?: number; w?: number; left: number; top: number } & SwingCfg> =
  ({ src, h, w, left, top, ...cfg }) => {
    const style = useSwing(cfg);
    const frame = useStep();
    if (frame < f(cfg.inAt) - 4 || frame > f(cfg.outAt) + 30) return null;
    return (
      <div style={{ position: "absolute", left, top, ...style }}>
        <Img src={staticFile(src)} style={{ ...(h ? { height: h } : {}), ...(w ? { width: w } : {}), filter: PROP_SHADOW }} />
      </div>
    );
  };

/* ══ MATERIAL A — the photo sticker (v12) ══════════════════════
   A photograph reads as PASTED because of a keyline that follows its alpha plus a hard
   shadow. The keyline is the same PNG flattened to a white silhouette, scaled up behind. */
export const Sticker: React.FC<{ src: string; h?: number; w?: number; grade?: string; ring?: number }> =
  ({ src, h, w, grade = "saturate(0.86) contrast(1.06) brightness(1.03) sepia(0.14)", ring = 1.035 }) => (
    <div style={{ position: "relative", display: "inline-block", filter: "drop-shadow(0 20px 20px rgba(72,46,16,0.38)) drop-shadow(0 3px 3px rgba(72,46,16,0.34))" }}>
      <Img src={staticFile(src)} style={{
        position: "absolute", left: "50%", top: "50%",
        ...(h ? { height: h } : {}), ...(w ? { width: w } : {}),
        transform: `translate(-50%,-50%) scale(${ring})`,
        filter: "brightness(0) invert(1)",
      }} />
      <Img src={staticFile(src)} style={{
        position: "relative", display: "block",
        ...(h ? { height: h } : {}), ...(w ? { width: w } : {}), filter: grade,
      }} />
    </div>
  );

/** A photographic sticker that LANDS — stiff spring, no idle sway. Evidence does not float. */
export const Evidence: React.FC<{
  src: string; h?: number; w?: number; left: number; top: number; tilt?: number; label?: string;
} & SwingCfg> = ({ src, h, w, left, top, tilt = 0, label, ...cfg }) => {
  const frame = useStep();
  const sIn = useSpringAt(cfg.inAt, 10, 190, 1.25);
  const sOut = useSpringAt(cfg.outAt, 200, 75, 0.5);
  const lab = useSpringAt(cfg.inAt + 0.45, 11, 170, 0.8);   // ⛔ hook above the gate
  if (frame < f(cfg.inAt) - 4 || frame > f(cfg.outAt) + 30) return null;
  const sign = cfg.from === "right" ? -1 : 1;
  return (
    <div style={{
      position: "absolute", left, top,
      transform: `translate(${sign * 60 * (1 - sIn)}px, ${-240 * (1 - sIn) + 200 * sOut}px) rotate(${tilt + 9 * sign * (1 - sIn) - 24 * sign * sOut}deg) scale(${0.94 + 0.06 * sIn - 0.1 * sOut})`,
      transformOrigin: "50% -14%",
      opacity: Math.min(1, sIn * 2.4) * (1 - Math.min(1, sOut * 1.4)),
    }}>
      <Sticker src={src} h={h} w={w} />
      {label && (
        <div style={{
          position: "absolute", left: "50%", bottom: -30, transform: `translateX(-50%) translateY(${(1 - lab) * 14}px)`,
          opacity: Math.min(1, lab * 1.6),
          fontFamily: deva, fontSize: 15, fontWeight: 700, wordSpacing: 3, whiteSpace: "nowrap",
          color: "#FFF6E4", background: INK, padding: "3px 12px", borderRadius: 2,
        }}>{label}</div>
      )}
    </div>
  );
};

/* ══ MATERIAL B — the torn paper card (v11) ════════════════════ */
export const tornClip = (seed: string, amp = 1.5, n = 14) => {
  const p: string[] = [];
  const j = (t: string, i: number) => random(`${seed}${t}${i}`) * amp;
  for (let i = 0; i <= n; i++) p.push(`${((i / n) * 100).toFixed(1)}% ${j("T", i).toFixed(2)}%`);
  for (let i = 1; i <= n; i++) p.push(`${(100 - j("R", i)).toFixed(2)}% ${((i / n) * 100).toFixed(1)}%`);
  for (let i = 1; i <= n; i++) p.push(`${(100 - (i / n) * 100).toFixed(1)}% ${(100 - j("B", i)).toFixed(2)}%`);
  for (let i = 1; i < n; i++) p.push(`${j("L", i).toFixed(2)}% ${(100 - (i / n) * 100).toFixed(1)}%`);
  return `polygon(${p.join(",")})`;
};

export const CARD_SHADOW = "drop-shadow(0 18px 20px rgba(72,46,16,0.34)) drop-shadow(0 2px 2px rgba(72,46,16,0.30))";

export const Card: React.FC<{
  seed: string; w?: number | string; h?: number | string; bg?: string; amp?: number;
  tilt?: number; pad?: string; style?: React.CSSProperties; children?: React.ReactNode;
}> = ({ seed, w, h, bg = PAPER, amp = 1.5, tilt = 0, pad = "0px", style, children }) => (
  <div style={{ position: "relative", width: w, height: h, transform: `rotate(${tilt}deg)`, ...style }}>
    <div style={{
      position: "absolute", inset: -4, background: PAPER_EDGE,
      clipPath: tornClip(seed + "core", amp * 1.25), transform: "rotate(0.7deg)", filter: CARD_SHADOW,
    }} />
    <div style={{
      position: "relative", width: "100%", height: "100%", background: bg,
      clipPath: tornClip(seed, amp), padding: pad, boxSizing: "border-box",
    }}>{children}</div>
  </div>
);

/* ══ MATERIAL C — the rig (v13) ════════════════════════════════
   Real Vox separates a figure into parts so pieces move independently. We do it WITHOUT
   separate assets: the same PNG is drawn once per band, each band clipped with inset() and
   rotated about its own BOTTOM edge, with a phase lag down the chain. Bands overlap by
   `bleed` so the seam never opens. Amplitudes stay under ~2° — past that the clip shows. */
export type RigBand = { from: number; to: number; amp: number; phase: number; pivot?: number };
export const RIG_HUMAN: RigBand[] = [
  { from: 0, to: 34, amp: 1.9, phase: 0, pivot: 100 },     // head + shoulders, leads
  { from: 30, to: 68, amp: 1.1, phase: 9, pivot: 100 },    // torso, follows
  { from: 64, to: 100, amp: 0.35, phase: 18, pivot: 100 }, // base, almost planted
];

export const Rigged: React.FC<{
  src: string; h: number; bands?: RigBand[]; speed?: number; bleed?: number; sticker?: boolean;
  grade?: string; breathe?: number;
}> = ({ src, h, bands = RIG_HUMAN, speed = 30, bleed = 2.2, sticker = false, grade, breathe = 0.006 }) => {
  const frame = useStep();
  const br = 1 + Math.sin(frame / 46) * breathe;
  return (
    // the shadow lives on the WRAPPER — one per figure. Shadowing each band instead would
    // draw a dark line at every seam.
    <div style={{
      position: "relative", height: h, transform: `scaleY(${br})`, transformOrigin: "50% 100%",
      filter: sticker ? undefined : PROP_SHADOW,
    }}>
      {bands.map((b, i) => {
        const a = Math.sin((frame - b.phase) / speed) * b.amp;
        const top = Math.max(0, b.from - (i === 0 ? 0 : bleed));
        const bot = Math.min(100, b.to + (i === bands.length - 1 ? 0 : bleed));
        return (
          <div key={i} style={{
            position: i === 0 ? "relative" : "absolute", inset: i === 0 ? undefined : 0,
            clipPath: `inset(${top}% 0 ${100 - bot}% 0)`,
            transform: `rotate(${a}deg)`,
            transformOrigin: `50% ${b.pivot ?? 100}%`,
          }}>
            {sticker
              ? <Sticker src={src} h={h} grade={grade} />
              : <Img src={staticFile(src)} style={{ display: "block", height: h }} />}
          </div>
        );
      })}
    </div>
  );
};

/* ══ THE GRAPHIC MATCH ═════════════════════════════════════════
   ONE marigold thread: draws as the bloodline, collapses to a single dot, rides through the
   shastra beat as that dot, reopens as the inheritance V. One object, three meanings. */
export const PersistentThread: React.FC<{ width?: number }> = ({ width = 4 }) => {
  const frame = useStep();
  const Y = 396, X0 = 372, X1 = 928, CX = 448, PY = 232, CY = 388;
  const draw = useKeys([[6.6, 0], [7.7, 1]]);
  const collapse = useKeys([[10.0, 0], [10.5, 1]]);
  const reopen = useKeys([[16.6, 0], [17.15, 1]]);
  const sway = Math.sin(frame / 21) * 9 * draw * (1 - collapse);
  if (frame < f(6.5)) return null;
  const dotX = interpolate(collapse, [0, 1], [(X0 + X1) / 2, 640]);
  const dotY = interpolate(collapse, [0, 1], [Y - 26, 300]);
  const parkX = interpolate(reopen, [0, 1], [dotX, CX]);
  const parkY = interpolate(reopen, [0, 1], [dotY, CY]);
  const lineEnd = X0 + (X1 - X0) * draw;
  return (
    <svg width={1280} height={720} style={{ position: "absolute", pointerEvents: "none" }}>
      <g opacity={1 - collapse}>
        <path d={`M${X0} ${Y} Q ${(X0 + X1) / 2} ${Y - 26 - sway} ${lineEnd} ${Y - (26 + sway) * Math.sin(Math.PI * draw)}`}
          fill="none" stroke={MARIGOLD} strokeWidth={width} strokeLinecap="round" />
        {[0.2, 0.4, 0.6, 0.8].map((t, i) => draw > t ? (
          <circle key={i} cx={X0 + (X1 - X0) * t} cy={Y - (26 + sway) * Math.sin(Math.PI * t)} r={7} fill="#FBF3E2" stroke={GOLD} strokeWidth={3} />
        ) : null)}
      </g>
      <circle cx={parkX} cy={parkY} r={7 * collapse * (1 - reopen) + 4 * reopen} fill={MARIGOLD} opacity={collapse} />
      <g opacity={reopen}>
        <line x1={CX} y1={CY} x2={CX - 110 * reopen} y2={CY - (CY - PY - 38) * reopen} stroke={MARIGOLD} strokeWidth={width} strokeLinecap="round" />
        <line x1={CX} y1={CY} x2={CX + 110 * reopen} y2={CY - (CY - PY - 38) * reopen} stroke={MARIGOLD} strokeWidth={width} strokeLinecap="round" />
      </g>
    </svg>
  );
};

export const Disc: React.FC<{ at: number; out: number; x: number; y: number; r: number; color: string; op?: number }> =
  ({ at, out, x, y, r, color, op = 0.14 }) => {
    const frame = useStep();
    const s = useSpringAt(at, 14, 55, 1.2);
    const o = useSpringAt(out, 200, 60, 0.7);
    const drift = interpolate(frame - f(at), [0, 200], [0, 46], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
    if (s <= 0.001 || frame > f(out) + 30) return null;
    return <div style={{
      position: "absolute", left: x - r, top: y - r, width: r * 2, height: r * 2, borderRadius: "50%",
      background: color, opacity: op * s * (1 - o),
      transform: `translateX(${(1 - s) * -260 + drift + o * 320}px) scale(${0.7 + s * 0.3})`,
    }} />;
  };

export const Furniture: React.FC<{ color?: string; op?: number }> = ({ color = INK, op = 0.6 }) => {
  const frame = useStep();
  const { durationInFrames } = useVideoConfig();
  const p = interpolate(frame, [0, durationInFrames], [0, 1]);
  return (
    <AbsoluteFill style={{ pointerEvents: "none" }}>
      <div style={{ position: "absolute", top: 42, left: 50, display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 4, height: 18, background: MARIGOLD, borderRadius: 2 }} />
        {/* ⛔ wordSpacing, never letterSpacing — tracking breaks the shirorekha */}
        <span style={{ fontFamily: deva, fontWeight: 700, fontSize: 17, wordSpacing: 7, color, opacity: op }}>सनातन डिकोड</span>
      </div>
      <div style={{ position: "absolute", left: 0, bottom: 0, height: 3, width: `${p * 100}%`, background: MARIGOLD }} />
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
